import { DOCUMENT } from '@angular/common';
import {
  booleanAttribute,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { auditTime, fromEvent, tap } from 'rxjs';

@Directive({
  selector: '[ptInfiniteScroll]',
})
export class InfiniteScrollDirective implements OnInit {
  readonly #elementRef = inject(ElementRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly #document = inject(DOCUMENT);

  scrollDirection = input<'horizontal' | 'vertical'>('vertical');
  threshold = input<number>(200);
  hasMore = input<boolean>(true);
  isLoading = input<boolean>(false);
  useGlobalScroll = input(false, { transform: booleanAttribute });

  scrolled = output<void>();
  scrollPositionChanged = output<number>();
  scrolledLeft = output<void>();
  scrolledRight = output<void>();

  #leftTriggered = false;
  #rightTriggered = false;

  ngOnInit(): void {
    const target = this.useGlobalScroll()
      ? this.#document
      : this.#elementRef.nativeElement;

    fromEvent<Event>(target, 'scroll')
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        auditTime(200),
        tap(({ target }) => {
          const { scrollingElement } = target as Document;
          this.#scroll((scrollingElement || target) as HTMLElement);
          this.#handleHorizontalScroll((scrollingElement || target) as HTMLElement);
        }),
      )
      .subscribe();
  }

  #scroll(target: HTMLElement): void {
    const { scrollTop, scrollHeight, clientHeight } = target;
    const scrollPosition = scrollTop + clientHeight;

    this.scrollPositionChanged.emit(scrollTop);

    if (this.isLoading() || !this.hasMore()) return;

    if (scrollPosition >= scrollHeight - this.threshold()) {
      this.scrolled.emit();
    }
  }

  #handleHorizontalScroll(target: HTMLElement): void {
    const { scrollLeft, scrollWidth, clientWidth } = target;
    const threshold = this.threshold();
    const atRightEdge = scrollLeft + clientWidth >= scrollWidth - threshold;
    const atLeftEdge = scrollLeft <= threshold;

    if (atRightEdge && !this.#rightTriggered && !this.isLoading()) {
      this.scrolledRight.emit();
      this.#rightTriggered = true;
    }

    if (atLeftEdge && !this.#leftTriggered && !this.isLoading()) {
      this.scrolledLeft.emit();
      this.#leftTriggered = true;
    }

    if (!atRightEdge) {
      this.#rightTriggered = false;
    }
    if (!atLeftEdge) {
      this.#leftTriggered = false;
    }
  }
}
